import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { SearchablePopoverShell, type Choice } from '../SearchablePopoverShell';
export type { Choice } from '../SearchablePopoverShell';

interface Props<C extends Choice> {
  className?: string;
  choices: C[];
  defaultChoice?: Pick<Choice, 'key'>;
  defaultValue?: string;
  name?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  renderChoice?(choice: C): React.ReactNode;
  onChange?(choice?: C): void;
  onInvalid?(e: React.InvalidEvent<HTMLSelectElement>): void;
  disabled?: boolean;
}

export function SearchableSelect<C extends Choice>(props: Props<C>) {
  const [selectedKey, setSelectedKey] = useState<C['key'] | undefined>(
    props.defaultValue || props.defaultChoice?.key,
  );

  const selected = selectedKey
    ? props.choices.find((choice) => choice.key === selectedKey)
    : undefined;
  const renderChoice = props.renderChoice || ((choice) => choice.label);

  useEffect(() => {
    props.onChange?.(selected);
  }, [props.onChange, selected]);

  const getFilteredChoices = (choices: C[], searchTerm: string) => {
    if (!searchTerm) return choices;

    const term = searchTerm.toLowerCase();
    return choices.filter(
      (choice) =>
        choice.label.toLowerCase().includes(term) ||
        choice.key.toLowerCase().includes(term),
    );
  };

  return (
    <>
      {props.name && selected && (
        <input type="hidden" name={props.name} value={selected.key} />
      )}
      <SearchablePopoverShell
        className={twMerge(
          'border-neutral-700 border rounded-xl focus:border-neutral-300 hover:border-neutral-500 disabled:cursor-not-allowed disabled:opacity-50',
          props.className,
        )}
        disabled={props.disabled}
        choices={props.choices}
        searchPlaceholder={props.searchPlaceholder}
        triggerContent={
          selected ? (
            <span>{renderChoice(selected)}</span>
          ) : (
            <div className="text-neutral-700">
              {props.placeholder || 'Select'}
            </div>
          )
        }
        getFilteredChoices={getFilteredChoices}
        getHighlightTerms={(searchTerm) => (searchTerm ? [searchTerm] : [])}
        onSelect={(choice, actions) => {
          setSelectedKey(choice.key);
          actions.setSearchTerm('');
          actions.setHighlightedIndex(0);
          actions.close();
        }}
      >
        {({
          filteredChoices,
          highlightedIndex,
          itemRefs,
          listRef,
          setHighlightedIndex,
          select,
          highlightMatch,
        }) => (
          <div ref={listRef} className="max-h-60 overflow-auto p-1">
            {filteredChoices.length === 0 ? (
              <div className="text-neutral-500 text-sm text-center py-4">
                No results found
              </div>
            ) : (
              filteredChoices.map((choice, index) => (
                <div
                  key={choice.key}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  onClick={() => {
                    select(choice);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={[
                    'px-4 py-2 cursor-pointer rounded-lg text-sm text-white',
                    'hover:bg-neutral-700',
                    index === highlightedIndex && 'bg-neutral-700',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {highlightMatch(choice.label)}
                </div>
              ))
            )}
          </div>
        )}
      </SearchablePopoverShell>
    </>
  );
}
